/**
 * 盤情報APIサービス
 * バックエンドAPIと通信して盤情報を取得・更新
 */

import { apiClient } from '@/shared/lib/axios';
import { logger } from '@/shared/lib/logger';
import { isAxiosError } from 'axios';
import { authService } from '@/features/auth/services/authService';
import { Panel, type PanelApiData } from '@/domain/entities/Panel';
import { PanelVisualization, type VisualizationApiData } from '@/domain/entities/PanelVisualization';
import { PanelMaintenance, type MaintenanceApiData } from '@/domain/entities/PanelMaintenance';
import { PanelErrorData, type ErrorDataApiData } from '@/domain/entities/PanelErrorData';
import type { RegisterDisplayItem, MaintenanceDisplayItem, ErrorDisplayItem } from '@/features/panels/constants/panelDetailTab.constants';

// ダッシュボードAPIサービスからステータスログ関数を再export
export { getStatusLogs as getPanelStatusLogs } from '@/features/dashboard/services/dashboardApiService';
export type { PanelStatusLogEntry, PanelStatusLogData } from '@/features/dashboard/types/dashboard.types';

// ========== 型定義 ==========

/**
 * APIから返される盤の生データ型
 *
 * ドメイン層のPanelApiDataをインフラストラクチャ層で再利用。
 */
type PanelApiResponse = PanelApiData;

/** フロントエンドで使用する盤基本情報（フォーマット済み表示文字列を含む） */
export interface PanelBasicInfo {
  readonly id: number;
  readonly panelName: string;
  readonly siteName: string;
  readonly status: 'NORMAL' | 'STOPPED' | 'ERROR' | null;
  readonly deviceGroupNumber: number | null;
  readonly deviceNumber: number | null;
  readonly deviceIdentificationDisplayText: string;
}

export interface PanelSettings {
  readonly id: number;
  readonly panelName: string;
}

interface GetPanelResponse {
  readonly success: boolean;
  readonly panel: PanelApiResponse;
}

interface GetAllPanelsResponse {
  readonly success: boolean;
  readonly panels: readonly PanelApiResponse[];
}

interface UpdatePanelNameRequest {
  readonly panelName: string;
}

interface UpdatePanelResponse {
  readonly success: boolean;
  readonly message: string;
  readonly panel: {
    readonly id: number;
    readonly panelName: string;
  };
}

interface GetPanelSettingsResponse {
  readonly success: boolean;
  readonly settings: PanelSettings;
}

interface UpdatePanelSettingsRequest {
  readonly panelName?: string;
}

interface UpdatePanelSettingsResponse {
  readonly success: boolean;
  readonly message: string;
  readonly panel: PanelSettings;
}

interface ExportTransmissionDataParams {
  readonly panelId: number;
  readonly startDate?: string;
  readonly endDate?: string;
}

interface ExportNoDataResponse {
  readonly success: boolean;
  readonly message: string;
  readonly recordCount: number;
  readonly fileCount: number;
}

interface GetVisualizationDataResponse {
  readonly success: boolean;
  readonly data: VisualizationApiData;
}

interface GetMaintenanceDataResponse {
  readonly success: boolean;
  readonly data: MaintenanceApiData;
}

interface GetErrorDataResponse {
  readonly success: boolean;
  readonly data: ErrorDataApiData;
}

// ========== API関数 ==========
/**
 * 全盤の基本情報を取得
 */
/**
 * APIレスポンスの生データをフォーマット済みのPanelBasicInfoに変換
 *
 * ドメインエンティティを経由して値オブジェクトの生成をドメイン層で完結させる。
 */
const mapPanelApiResponseToPanelBasicInfo = (panelApiResponse: PanelApiResponse): PanelBasicInfo => {
  const panelEntity = Panel.createFromApiResponse(panelApiResponse);
  return panelEntity.toPresentationModel();
};

export const getAllPanels = async (): Promise<readonly PanelBasicInfo[]> => {
  try {
    const response = await apiClient.get<GetAllPanelsResponse>('/api/panels');

    if (response.data.success) {
      return response.data.panels.map(mapPanelApiResponseToPanelBasicInfo);
    }

    throw new Error('盤一覧の取得に失敗しました');
  } catch (error) {
    logger.captureError(error, 'panelApiService.getAllPanels');
    throw error;
  }
};

/**
 * 特定盤の基本情報を取得
 */
export const getPanelById = async ({
  panelId,
}: {
  panelId: number;
}): Promise<PanelBasicInfo | null> => {
  try {
    const response = await apiClient.get<GetPanelResponse>(`/api/panels/${panelId}`);

    if (response.data.success) {
      return mapPanelApiResponseToPanelBasicInfo(response.data.panel);
    }

    return null;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    logger.captureError(error, `panelApiService.getPanelById(${panelId})`);
    throw error;
  }
};

/**
 * 盤名を更新
 */
export const updatePanelName = async ({
  panelId,
  newPanelName,
}: {
  panelId: number;
  newPanelName: string;
}): Promise<UpdatePanelResponse> => {
  try {
    const requestBody: UpdatePanelNameRequest = {
      panelName: newPanelName
    };

    const response = await apiClient.put<UpdatePanelResponse>(
      `/api/panels/${panelId}`,
      requestBody
    );

    if (response.data.success !== true) {
      throw new Error(response.data.message || '盤名の更新に失敗しました');
    }

    return response.data;
  } catch (error) {
    logger.captureError(error, `panelApiService.updatePanelName(${panelId})`);
    throw error;
  }
};

/**
 * 盤設定を取得
 */
export const getPanelSettings = async ({
  panelId,
}: {
  panelId: number;
}): Promise<PanelSettings | null> => {
  try {
    const response = await apiClient.get<GetPanelSettingsResponse>(`/api/panels/${panelId}/settings`);

    if (response.data.success) {
      return response.data.settings;
    }

    return null;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    logger.captureError(error, `panelApiService.getPanelSettings(${panelId})`);
    throw error;
  }
};

/**
 * 盤設定を更新
 */
export const updatePanelSettings = async ({
  panelId,
  settings,
}: {
  panelId: number;
  settings: {
    panelName?: string;
  };
}): Promise<UpdatePanelSettingsResponse> => {
  try {
    const requestBody: UpdatePanelSettingsRequest = settings;

    // CSRFトークンを取得
    const csrfToken = await authService.getCsrfToken();

    const response = await apiClient.patch<UpdatePanelSettingsResponse>(
      `/api/panels/${panelId}/settings`,
      requestBody,
      {
        headers: {
          'x-csrf-token': csrfToken,
        },
      }
    );

    if (response.data.success !== true) {
      throw new Error(response.data.message || '盤設定の更新に失敗しました');
    }

    return response.data;
  } catch (error) {
    logger.captureError(error, `panelApiService.updatePanelSettings(${panelId})`);
    throw error;
  }
};

/**
 * 盤送信データをCSV形式でエクスポート
 * 期間指定がない場合は全期間のデータをエクスポート
 */
export const exportTransmissionData = async (
  params: ExportTransmissionDataParams
): Promise<{ success: boolean; blob?: Blob; message?: string }> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.startDate !== undefined) {
      queryParams.append('startDate', params.startDate);
    }
    if (params.endDate !== undefined) {
      queryParams.append('endDate', params.endDate);
    }

    const queryString = queryParams.toString();
    const url = `/api/panels/${params.panelId}/export${queryString !== '' ? `?${queryString}` : ''}`;

    const response = await apiClient.get(url, {
      responseType: 'blob',
    });

    // Content-Typeをチェックしてデータの有無を判定
    const contentType = response.headers['content-type'] || '';

    if (contentType.includes('application/json')) {
      // JSONレスポンスの場合（データなしの場合）
      const text = await response.data.text();
      const jsonResponse = JSON.parse(text) as ExportNoDataResponse;
      return {
        success: true,
        message: jsonResponse.message,
      };
    }

    // CSVデータの場合
    return {
      success: true,
      blob: response.data as Blob,
    };
  } catch (error) {
    logger.captureError(error, `panelApiService.exportTransmissionData(${params.panelId})`);
    throw error;
  }
};

/**
 * 最新の見える化データを取得
 *
 * ドメインエンティティを経由して値オブジェクトの生成をドメイン層で完結させる。
 */
export const getLatestVisualizationData = async ({
  panelId,
}: {
  panelId: number;
}): Promise<readonly RegisterDisplayItem[]> => {
  try {
    const response = await apiClient.get<GetVisualizationDataResponse>(
      `/api/panels/${panelId}/visualization-data`
    );

    if (response.data.success) {
      const entity = PanelVisualization.createFromApiResponse(response.data.data);
      return entity.toPresentationModel();
    }

    throw new Error('見える化データの取得に失敗しました');
  } catch (error) {
    logger.captureError(error, `panelApiService.getLatestVisualizationData(${panelId})`);
    throw error;
  }
};

/**
 * 最新のメンテナンスデータを取得
 *
 * ドメインエンティティを経由して値オブジェクトの生成をドメイン層で完結させる。
 */
export const getLatestMaintenanceData = async ({
  panelId,
}: {
  panelId: number;
}): Promise<readonly MaintenanceDisplayItem[]> => {
  try {
    const response = await apiClient.get<GetMaintenanceDataResponse>(
      `/api/panels/${panelId}/maintenance-data`
    );

    if (response.data.success) {
      const entity = PanelMaintenance.createFromApiResponse(response.data.data);
      return entity.toPresentationModel();
    }

    throw new Error('メンテナンスデータの取得に失敗しました');
  } catch (error) {
    logger.captureError(error, `panelApiService.getLatestMaintenanceData(${panelId})`);
    throw error;
  }
};

/**
 * 最新のエラーデータを取得
 *
 * ドメインエンティティを経由して値オブジェクトの生成をドメイン層で完結させる。
 */
export const getLatestErrorData = async ({
  panelId,
}: {
  panelId: number;
}): Promise<readonly ErrorDisplayItem[]> => {
  try {
    const response = await apiClient.get<GetErrorDataResponse>(
      `/api/panels/${panelId}/error-data`
    );

    if (response.data.success) {
      const entity = PanelErrorData.createFromApiResponse(response.data.data);
      return entity.toPresentationModel();
    }

    throw new Error('エラーデータの取得に失敗しました');
  } catch (error) {
    logger.captureError(error, `panelApiService.getLatestErrorData(${panelId})`);
    throw error;
  }
};